import React, { useState, useEffect, useMemo } from "react";
import { FiX, FiPlus, FiBookOpen, FiEdit3 } from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import { formatAmountWithCommas } from "../../../../Utils/utils";
import { rateLibraryActivities, stockMaterialsList } from "./boqService";

const UNITS = ["Cft", "Sft", "Rft", "Kg", "Ton", "Nos", "Bags", "Lump Sum", "Sq. M", "Cu. M"];

const BOQItemDrawer = ({
  open,
  onClose,
  onSave,
  editItem = null,
  existingSections = [],
  defaultSection = "",
}) => {
  const [activeTab, setActiveTab] = useState("basic"); // "basic" | "consumptions"
  const [itemSource, setItemSource] = useState("library"); // "library" | "manual"
  const [selectedActivity, setSelectedActivity] = useState("");

  // Form Fields
  const [section, setSection] = useState("");
  const [newSectionTitle, setNewSectionTitle] = useState("");
  const [isAddingNewSection, setIsAddingNewSection] = useState(false);
  const [itemCode, setItemCode] = useState("");
  const [description, setDescription] = useState("");
  const [specification, setSpecification] = useState("");
  const [qty, setQty] = useState(100);
  const [unit, setUnit] = useState("Cft");
  const [unitRate, setUnitRate] = useState(12000);
  const [rateSource, setRateSource] = useState("Library");

  // Material Consumptions in Tab 2
  const [consumptions, setConsumptions] = useState([]);

  // Populate fields when drawer opens or editItem changes
  useEffect(() => {
    if (editItem) {
      setItemCode(editItem.itemCode || "");
      setDescription(editItem.description || "");
      setSpecification(editItem.specification || "");
      setQty(Number(editItem.qty) || 0);
      setUnit(editItem.unit || "Cft");
      setUnitRate(Number(editItem.unitRate) || 0);
      setRateSource(editItem.rateSource || "Library");
      setItemSource(editItem.rateSource === "Manual" ? "manual" : "library");
      setSection(editItem.sectionTitle || defaultSection || existingSections[0]?.title || "");
      setIsAddingNewSection(false);
      setNewSectionTitle("");
      setConsumptions(
        editItem.consumptions ? JSON.parse(JSON.stringify(editItem.consumptions)) : [],
      );
      setSelectedActivity("");
    } else {
      // New Item Defaults
      const initSec = defaultSection || existingSections[0]?.title || "A. Earthworks";
      setSection(initSec);
      setIsAddingNewSection(false);
      setNewSectionTitle("");
      setItemSource("library");
      setSelectedActivity(rateLibraryActivities[0]?.name || "");

      // Auto-fill from first activity preset
      const act = rateLibraryActivities[0];
      if (act) {
        setItemCode(act.itemCode);
        setDescription(act.description);
        setSpecification(act.specification);
        setUnit(act.unit);
        setUnitRate(act.unitRate);
        setRateSource(act.rateSource);
        setConsumptions(JSON.parse(JSON.stringify(act.consumptions || [])));
      } else {
        setItemCode("");
        setDescription("");
        setSpecification("");
        setUnit("Cft");
        setUnitRate(0);
        setRateSource("Library");
        setConsumptions([]);
      }
      setQty(100);
    }
    setActiveTab("basic");
  }, [open, editItem, defaultSection, existingSections]);

  // Handle Rate Library selection
  const handleSelectActivity = (actName) => {
    setSelectedActivity(actName);
    const act = rateLibraryActivities.find((a) => a.name === actName);
    if (act) {
      if (act.section && existingSections.some((s) => s.title === act.section)) {
        setSection(act.section);
      }
      setItemCode(act.itemCode);
      setDescription(act.description);
      setSpecification(act.specification);
      setUnit(act.unit);
      setUnitRate(act.unitRate);
      setRateSource("Library");
      setConsumptions(JSON.parse(JSON.stringify(act.consumptions || [])));
    }
  };

  // Switch between Rate Library & Manual
  const handleSourceChange = (source) => {
    setItemSource(source);
    if (source === "manual") {
      setRateSource("Manual");
      setSelectedActivity("");
    } else {
      setRateSource("Library");
      if (rateLibraryActivities[0]) {
        handleSelectActivity(rateLibraryActivities[0].name);
      }
    }
  };

  // Real-time calculated Amount
  const amount = useMemo(() => {
    return (Number(qty) || 0) * (Number(unitRate) || 0);
  }, [qty, unitRate]);

  // Consumption management
  const handleAddConsumptionRow = () => {
    const firstMat = stockMaterialsList[0];
    const newRow = {
      id: `cons-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      materialId: firstMat.id,
      materialName: firstMat.label,
      baseQty: 10,
      unit: firstMat.unit,
      wastagePct: firstMat.defaultWastage,
      totalReq: Number((10 * (1 + firstMat.defaultWastage / 100)).toFixed(2)),
    };
    setConsumptions((prev) => [...prev, newRow]);
  };

  const handleUpdateConsumption = (index, field, value) => {
    setConsumptions((prev) => {
      const copy = [...prev];
      const row = { ...copy[index] };

      if (field === "materialId") {
        const mat = stockMaterialsList.find((m) => m.id === value);
        if (mat) {
          row.materialId = mat.id;
          row.materialName = mat.label;
          row.unit = mat.unit;
          row.wastagePct = mat.defaultWastage;
        }
      } else if (field === "baseQty") {
        row.baseQty = parseFloat(value) || 0;
      } else if (field === "wastagePct") {
        row.wastagePct = parseFloat(value) || 0;
      } else if (field === "unit") {
        row.unit = value;
      }

      // Recompute totalReq: BaseQty * (1 + WastagePct / 100)
      const base = Number(row.baseQty) || 0;
      const wastage = Number(row.wastagePct) || 0;
      row.totalReq = Number((base * (1 + wastage / 100)).toFixed(2));

      copy[index] = row;
      return copy;
    });
  };

  const handleRemoveConsumption = (index) => {
    setConsumptions((prev) => prev.filter((_, idx) => idx !== index));
  };

  // Validation & Save
  const handleSaveItem = (addAnother = false) => {
    const finalSection = isAddingNewSection ? newSectionTitle.trim() : section;
    if (!finalSection) {
      toast.error("Please select or enter a section");
      setActiveTab("basic");
      return;
    }
    if (!itemCode.trim()) {
      toast.error("Item Code is required");
      setActiveTab("basic");
      return;
    }
    if (!description.trim()) {
      toast.error("Item Description is required");
      setActiveTab("basic");
      return;
    }
    if (!qty || Number(qty) <= 0) {
      toast.error("Quantity must be greater than zero");
      setActiveTab("basic");
      return;
    }

    const itemData = {
      id: editItem?.id || `item-${Date.now()}`,
      sectionTitle: finalSection,
      itemCode: itemCode.trim(),
      description: description.trim(),
      specification: specification.trim(),
      qty: Number(qty),
      unit,
      unitRate: Number(unitRate),
      amount,
      rateSource,
      consumptions: consumptions.map((c) => ({
        ...c,
        totalReq: Number(
          ((Number(c.baseQty) || 0) * (1 + (Number(c.wastagePct) || 0) / 100)).toFixed(2),
        ),
      })),
    };

    onSave(itemData, addAnother);

    if (addAnother) {
      toast.success(`Item "${itemCode}" saved. Ready for next item.`);
      setItemCode("");
      setDescription("");
      setSpecification("");
      setQty(100);
      setUnitRate(0);
      setConsumptions([]);
      setActiveTab("basic");
    } else {
      toast.success(`Item "${itemCode}" saved successfully`);
      onClose();
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        backgroundColor: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Drawer Header */}
      <div
        className="d-flex align-items-center justify-content-between px-4 py-3"
        style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: "#ffffff" }}
      >
        <div className="d-flex align-items-center" style={{ gap: "10px" }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "32px",
              borderRadius: "6px",
              backgroundColor: "#e0f2fe",
              color: "#0369a1",
            }}
          >
            {editItem ? <FiEdit3 size={18} /> : <FiPlus size={18} />}
          </span>
          <h5 className="m-0" style={{ fontWeight: 700, color: "#111827", fontSize: "17px" }}>
            {editItem ? `Edit Item: ${editItem.itemCode}` : "Add BOQ Item"}
          </h5>
        </div>
        <button
          type="button"
          className="btn btn-link p-0 text-muted"
          onClick={onClose}
          style={{
            fontSize: "14px",
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <span>Close</span>
          <FiX size={18} />
        </button>
      </div>

      {/* Drawer Tabs (Header Tabs matching image 2 & 3) */}
      <div
        className="d-flex px-3 pt-2"
        style={{
          borderBottom: "1px solid #e5e7eb",
          backgroundColor: "#f9fafb",
          gap: "6px",
        }}
      >
        <button
          type="button"
          onClick={() => setActiveTab("basic")}
          style={{
            flex: 1,
            padding: "10px 14px",
            border: "none",
            borderBottom: activeTab === "basic" ? "3px solid #056363" : "3px solid transparent",
            backgroundColor: activeTab === "basic" ? "#ffffff" : "transparent",
            color: activeTab === "basic" ? "#056363" : "#64748b",
            fontWeight: activeTab === "basic" ? 700 : 500,
            fontSize: "13px",
            textAlign: "left",
            cursor: "pointer",
            borderRadius: "6px 6px 0 0",
            boxShadow: activeTab === "basic" ? "0 -2px 6px rgba(0,0,0,0.03)" : "none",
            transition: "all 0.15s ease",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: "13px" }}>Basic Info</div>
          <div
            style={{
              fontSize: "11px",
              color: activeTab === "basic" ? "#0d9488" : "#94a3b8",
              fontWeight: 400,
            }}
          >
            Standard details
          </div>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("consumptions")}
          style={{
            flex: 1,
            padding: "10px 14px",
            border: "none",
            borderBottom:
              activeTab === "consumptions" ? "3px solid #056363" : "3px solid transparent",
            backgroundColor: activeTab === "consumptions" ? "#ffffff" : "transparent",
            color: activeTab === "consumptions" ? "#056363" : "#64748b",
            fontWeight: activeTab === "consumptions" ? 700 : 500,
            fontSize: "13px",
            textAlign: "left",
            cursor: "pointer",
            borderRadius: "6px 6px 0 0",
            boxShadow: activeTab === "consumptions" ? "0 -2px 6px rgba(0,0,0,0.03)" : "none",
            transition: "all 0.15s ease",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: "13px" }}>Material Consumptions</div>
          <div
            style={{
              fontSize: "11px",
              color: activeTab === "consumptions" ? "#0d9488" : "#94a3b8",
              fontWeight: 400,
            }}
          >
            Slide - over Drawer
          </div>
        </button>
      </div>

      {/* Tab 1: Basic Info */}
      {activeTab === "basic" && (
        <div
          className="px-4 py-3 flex-grow-1"
          style={{
            overflowY: "auto",
            fontSize: "13px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {/* Item Source Radio Buttons */}
          <div>
            <label
              className="d-block mb-2 font-weight-bold"
              style={{ color: "#374151", fontSize: "12.5px" }}
            >
              Item Source
            </label>
            <div className="d-flex align-items-center" style={{ gap: "20px" }}>
              <label
                className="d-inline-flex align-items-center m-0 cur_sor"
                style={{ cursor: "pointer" }}
              >
                <input
                  type="radio"
                  name="itemSource"
                  value="library"
                  checked={itemSource === "library"}
                  onChange={() => handleSourceChange("library")}
                  style={{
                    accentColor: "#056363",
                    width: "16px",
                    height: "16px",
                    marginRight: "6px",
                  }}
                />
                <span
                  style={{
                    fontSize: "13px",
                    color: itemSource === "library" ? "#056363" : "#4b5563",
                    fontWeight: itemSource === "library" ? 600 : 400,
                  }}
                >
                  From Rate Library
                </span>
              </label>

              <label
                className="d-inline-flex align-items-center m-0 cur_sor"
                style={{ cursor: "pointer" }}
              >
                <input
                  type="radio"
                  name="itemSource"
                  value="manual"
                  checked={itemSource === "manual"}
                  onChange={() => handleSourceChange("manual")}
                  style={{
                    accentColor: "#056363",
                    width: "16px",
                    height: "16px",
                    marginRight: "6px",
                  }}
                />
                <span
                  style={{
                    fontSize: "13px",
                    color: itemSource === "manual" ? "#056363" : "#4b5563",
                    fontWeight: itemSource === "manual" ? 600 : 400,
                  }}
                >
                  Custom / Manual Item
                </span>
              </label>
            </div>
          </div>

          {/* Select Construction Activity (When From Rate Library) */}
          {itemSource === "library" && (
            <div
              style={{
                backgroundColor: "#f0fdfa",
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #ccfbf1",
              }}
            >
              <label
                className="d-flex align-items-center justify-content-between mb-1 font-weight-bold"
                style={{ color: "#0f766e", fontSize: "12px" }}
              >
                <span className="d-flex align-items-center" style={{ gap: "6px" }}>
                  <FiBookOpen size={14} /> Select Construction Activity *
                </span>
                <span
                  className="badge badge-light border text-muted"
                  style={{ fontSize: "10.5px" }}
                >
                  Auto-fills Specs & Rates
                </span>
              </label>
              <select
                className="form-control form-control-sm"
                value={selectedActivity}
                onChange={(e) => handleSelectActivity(e.target.value)}
                style={{
                  borderRadius: "6px",
                  fontSize: "13px",
                  height: "36px",
                  borderColor: "#99f6e4",
                }}
              >
                <option value="">-- Choose activity from library --</option>
                {rateLibraryActivities.map((act) => (
                  <option key={act.id} value={act.name}>
                    {act.name} (PKR {formatAmountWithCommas(act.unitRate)} / {act.unit})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Section Selection */}
          <div>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label
                className="m-0 font-weight-bold"
                style={{ color: "#374151", fontSize: "12.5px" }}
              >
                Section *
              </label>
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={() => setIsAddingNewSection(!isAddingNewSection)}
                style={{ fontSize: "11.5px", color: "#056363", textDecoration: "none" }}
              >
                {isAddingNewSection ? "Select existing section" : "+ New section"}
              </button>
            </div>

            {isAddingNewSection ? (
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="e.g. G. External Facade & Cladding"
                value={newSectionTitle}
                onChange={(e) => setNewSectionTitle(e.target.value)}
                style={{ borderRadius: "6px", fontSize: "13px", height: "36px" }}
              />
            ) : (
              <select
                className="form-control form-control-sm"
                value={section}
                onChange={(e) => setSection(e.target.value)}
                style={{ borderRadius: "6px", fontSize: "13px", height: "36px" }}
              >
                {existingSections.map((sec, idx) => (
                  <option key={idx} value={sec.title}>
                    {sec.title}
                  </option>
                ))}
                {!existingSections.some((s) => s.title === section) && section && (
                  <option value={section}>{section}</option>
                )}
              </select>
            )}
          </div>

          {/* Item Code */}
          <div>
            <label
              className="d-block mb-1 font-weight-bold"
              style={{ color: "#374151", fontSize: "12.5px" }}
            >
              Item Code *
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="e.g. B.1"
              value={itemCode}
              onChange={(e) => setItemCode(e.target.value)}
              style={{ borderRadius: "6px", fontSize: "13px", height: "36px" }}
            />
          </div>

          {/* Item Description */}
          <div>
            <label
              className="d-block mb-1 font-weight-bold"
              style={{ color: "#374151", fontSize: "12.5px" }}
            >
              Item Description *
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="e.g. PCC (1:4:8)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ borderRadius: "6px", fontSize: "13px", height: "36px" }}
            />
          </div>

          {/* Specification */}
          <div>
            <label
              className="d-block mb-1 font-weight-bold"
              style={{ color: "#374151", fontSize: "12.5px" }}
            >
              Specification *
            </label>
            <textarea
              className="form-control form-control-sm"
              rows={3}
              placeholder="50mm thick, on foundations and level surfaces as specified"
              value={specification}
              onChange={(e) => setSpecification(e.target.value)}
              style={{ borderRadius: "6px", fontSize: "13px" }}
            />
          </div>

          {/* Quantity and Unit in 2 columns */}
          <div className="row">
            <div className="col-7 pr-2">
              <label
                className="d-block mb-1 font-weight-bold"
                style={{ color: "#374151", fontSize: "12.5px" }}
              >
                Quantity *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control form-control-sm"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                style={{ borderRadius: "6px", fontSize: "13px", height: "36px" }}
              />
            </div>

            <div className="col-5 pl-2">
              <label
                className="d-block mb-1 font-weight-bold"
                style={{ color: "#374151", fontSize: "12.5px" }}
              >
                Unit *
              </label>
              <select
                className="form-control form-control-sm"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                style={{ borderRadius: "6px", fontSize: "13px", height: "36px" }}
              >
                {UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Unit Rate and Rate Source */}
          <div className="row">
            <div className="col-7 pr-2">
              <label
                className="d-block mb-1 font-weight-bold"
                style={{ color: "#374151", fontSize: "12.5px" }}
              >
                Unit Rate (PKR) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control form-control-sm"
                value={unitRate}
                onChange={(e) => setUnitRate(e.target.value)}
                style={{ borderRadius: "6px", fontSize: "13px", height: "36px" }}
              />
            </div>

            <div className="col-5 pl-2">
              <label
                className="d-block mb-1 font-weight-bold"
                style={{ color: "#374151", fontSize: "12.5px" }}
              >
                Rate Source
              </label>
              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  height: "36px",
                  borderRadius: "6px",
                  backgroundColor: rateSource === "Library" ? "#f0fdf4" : "#fef3c7",
                  border: rateSource === "Library" ? "1px solid #bbf7d0" : "1px solid #fde68a",
                  color: rateSource === "Library" ? "#166534" : "#92400e",
                  fontWeight: 600,
                  fontSize: "12.5px",
                }}
              >
                {rateSource}
              </div>
            </div>
          </div>

          {/* Calculated Amount Display Box */}
          <div
            className="p-3"
            style={{
              backgroundColor: "#f8fafc",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}
          >
            <div className="text-muted" style={{ fontSize: "12px", marginBottom: "2px" }}>
              Amount (PKR)
            </div>
            <div style={{ fontSize: "20px", fontWeight: 700, color: "#056363" }}>
              PKR {formatAmountWithCommas(amount)}
            </div>
            <div className="text-muted" style={{ fontSize: "11px", marginTop: "2px" }}>
              = {qty || 0} {unit} × PKR {formatAmountWithCommas(unitRate || 0)}
            </div>
          </div>
        </div>
      )}

      {/* Tab 2: Material Consumptions */}
      {activeTab === "consumptions" && (
        <div
          className="px-4 py-3 flex-grow-1"
          style={{
            overflowY: "auto",
            fontSize: "13px",
            display: "flex",
            flexDirection: "column",
            gap: "14px",
          }}
        >
          <div>
            <h6 className="m-0 font-weight-bold" style={{ color: "#111827", fontSize: "14px" }}>
              Expected Material Consumptions (The Reconciliation Link)
            </h6>
          </div>

          {/* Dynamic Alert Banner referencing Qty and Unit from Tab 1 */}
          <div
            style={{
              backgroundColor: "#fffbeb",
              borderLeft: "4px solid #f59e0b",
              padding: "10px 14px",
              borderRadius: "4px",
              color: "#92400e",
              fontSize: "12.5px",
              lineHeight: "1.4",
            }}
          >
            Define what and how much material is required to complete{" "}
            <strong>
              {qty || 0} {unit}
            </strong>{" "}
            of this item.
          </div>

          {/* Consumptions List */}
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {consumptions.length === 0 ? (
              <div
                className="text-center py-4 px-3"
                style={{
                  backgroundColor: "#f8fafc",
                  borderRadius: "8px",
                  border: "1px dashed #cbd5e1",
                  color: "#64748b",
                }}
              >
                <p className="mb-2" style={{ fontSize: "13px" }}>
                  No material consumptions linked yet.
                </p>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={handleAddConsumptionRow}
                  style={{
                    backgroundColor: "#056363",
                    color: "#fff",
                    fontSize: "12px",
                    borderRadius: "6px",
                    padding: "4px 12px",
                  }}
                >
                  + Add Expected Material
                </button>
              </div>
            ) : (
              consumptions.map((row, idx) => (
                <div
                  key={row.id || idx}
                  style={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "12px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  }}
                >
                  {/* Material SKU Select */}
                  <div className="mb-2">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label
                        className="m-0 font-weight-bold"
                        style={{ fontSize: "11.5px", color: "#374151" }}
                      >
                        Material / SKU *
                      </label>
                      <MdDeleteForever
                        title="Delete Material"
                        size="1.2em"
                        className="cur_sor"
                        style={{ cursor: "pointer", color: "#c0392b" }}
                        onClick={() => handleRemoveConsumption(idx)}
                      />
                    </div>
                    <select
                      className="form-control form-control-sm"
                      value={row.materialId}
                      onChange={(e) => handleUpdateConsumption(idx, "materialId", e.target.value)}
                      style={{ borderRadius: "6px", fontSize: "12px", height: "34px" }}
                    >
                      {stockMaterialsList.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.label} ({m.unit})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Base Qty, Unit, Wastage %, Total Req */}
                  <div className="row align-items-center">
                    <div className="col-4 pr-1">
                      <label
                        className="d-block mb-1 font-weight-bold"
                        style={{ fontSize: "11px", color: "#64748b" }}
                      >
                        Base Qty *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control form-control-sm text-center"
                        value={row.baseQty}
                        onChange={(e) => handleUpdateConsumption(idx, "baseQty", e.target.value)}
                        style={{ borderRadius: "5px", fontSize: "12px", height: "32px" }}
                      />
                    </div>

                    <div className="col-2 px-1">
                      <label
                        className="d-block mb-1 font-weight-bold"
                        style={{ fontSize: "11px", color: "#64748b" }}
                      >
                        Unit
                      </label>
                      <span
                        className="d-flex align-items-center justify-content-center text-muted"
                        style={{ height: "32px", fontSize: "11.5px", fontWeight: 500 }}
                      >
                        {row.unit}
                      </span>
                    </div>

                    <div className="col-3 px-1">
                      <label
                        className="d-block mb-1 font-weight-bold"
                        style={{ fontSize: "11px", color: "#64748b" }}
                      >
                        Wastage % *
                      </label>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="100"
                        className="form-control form-control-sm text-center"
                        value={row.wastagePct}
                        onChange={(e) => handleUpdateConsumption(idx, "wastagePct", e.target.value)}
                        style={{ borderRadius: "5px", fontSize: "12px", height: "32px" }}
                      />
                    </div>

                    <div className="col-3 pl-1 text-right">
                      <label
                        className="d-block mb-1 font-weight-bold text-right"
                        style={{ fontSize: "11px", color: "#0369a1" }}
                      >
                        Total Req.
                      </label>
                      <div
                        style={{
                          height: "32px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          fontWeight: 700,
                          color: "#0f766e",
                          fontSize: "13px",
                        }}
                      >
                        {row.totalReq || 0}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}

            {/* Add expected material button */}
            {consumptions.length > 0 && (
              <button
                type="button"
                className="btn btn-link p-0 text-left d-inline-flex align-items-center mt-1"
                onClick={handleAddConsumptionRow}
                style={{
                  color: "#056363",
                  fontSize: "13px",
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                <FiPlus className="mr-1" /> Add Expected Material
              </button>
            )}
          </div>

          {/* Footnote matching image 3 */}
          <div
            className="text-muted mt-2"
            style={{
              fontSize: "11.5px",
              lineHeight: "1.4",
              fontStyle: "italic",
              borderTop: "1px dashed #e2e8f0",
              paddingTop: "8px",
            }}
          >
            Note: These quantities establish the max draw-down allowance for Site Store material
            issue checks.
          </div>
        </div>
      )}

      {/* Pinned Action Buttons Footer */}
      <div
        className="d-flex align-items-center justify-content-end px-4 py-3"
        style={{
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#f8fafc",
          gap: "10px",
        }}
      >
        <ActionButton text="Cancel" variant="cancel" onClick={onClose} />

        {activeTab === "basic" && !editItem && (
          <ActionButton
            text="Save & Add Another"
            variant="grey"
            onClick={() => handleSaveItem(true)}
          />
        )}

        <ActionButton
          text={activeTab === "consumptions" ? "Save Consumptions" : "Save Item"}
          variant="save"
          onClick={() => handleSaveItem(false)}
        />
      </div>
    </div>
  );
};

export default BOQItemDrawer;
